const RECOVERED_BUILD_KEY = 'wms:recovered-chunk-build';

let recoverCurrentBuild: (() => boolean) | null = null;

interface ChunkLoadRecoveryOptions {
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  buildId: string;
}

export function registerChunkLoadRecovery({
  target,
  storage,
  reload,
  buildId,
}: ChunkLoadRecoveryOptions): () => void {
  const attemptRecovery = () => {
    try {
      if (storage.getItem(RECOVERED_BUILD_KEY) === buildId) return false;

      storage.setItem(RECOVERED_BUILD_KEY, buildId);
    } catch {
      return false;
    }

    reload();
    return true;
  };

  const handlePreloadError = (event: Event) => {
    event.preventDefault();
    attemptRecovery();
  };

  recoverCurrentBuild = attemptRecovery;
  target.addEventListener('vite:preloadError', handlePreloadError);

  return () => {
    target.removeEventListener('vite:preloadError', handlePreloadError);
    if (recoverCurrentBuild === attemptRecovery) recoverCurrentBuild = null;
  };
}

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  return /(?:dynamically imported module|module script|loading chunk|importing a module script)/i.test(
    error.message,
  );
}

export function recoverFromChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  return recoverCurrentBuild?.() ?? false;
}
